// Access grants (#418): what an organisation asked to do in this workspace, answered
// one capability at a time.
//
// Each request is its own row and its own answer. Granting is standing authority,
// held until revoked, so it takes a step-up — the same class the design protects
// for an "always" consent (owner's Q5, 2026-09-12), proved through the shared
// step-up dialog with whichever factor the user holds (#729). Declining and revoking take
// none: taking authority away is never gated. The words for each capability are
// the customers screen's (`CAPABILITY_TEXT`), so what was asked and what is
// answered read the same on both sides.

import { readingAsOrg, type OrgKind } from "../api/memberships";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";

import { CAPABILITY_TEXT } from "../api/customers";
import type { GrantRow, GrantsRead } from "../api/grants";
import {
  GRANT_STEP_UP_PURPOSE,
  investigateLiveIn,
  NEEDS_INVESTIGATE,
  NO_EFFECT_YET,
  OPENS_AUDIT_TRAIL,
  orderedCapabilities,
  waitsOnInvestigate,
} from "../api/grants";
import { messageOf } from "../api/http";
import type { PasskeyChallenge } from "../api/passkeys";
import type { StepUp } from "../api/policy";
import type { SessionResponse } from "../api/session";
import { Chip, Empty, Notice, PageIntro } from "./chrome";
import { useReadOnly } from "./readOnly";
import {
  StepUpDialog,
  useStepUpAttempt,
  useStepUpFactors,
  type BeginPasskey,
  type FactorsReading,
  type StepUpFactors,
} from "./StepUp";

export type OnAnswer = (
  grantId: string,
  decision: "granted" | "declined",
  stepUp: StepUp | null,
) => Promise<void>;

export function AccessGrantsPage({
  session,
  orgKind = null,
  grants,
  error,
  onAnswer,
  onRevoke,
  stepUpFactors,
  beginPasskey,
}: {
  session: SessionResponse;
  grants: GrantsRead | null;
  error: string | null;
  onAnswer: OnAnswer;
  onRevoke: (grantId: string) => Promise<void>;
  /** `GET /auth/mfa`'s factors, for a grant's step-up (#729). */
  stepUpFactors: () => Promise<StepUpFactors>;
  /** Mint the assertion challenge for a passkey step-up (#729). */
  beginPasskey?: (purpose: string) => Promise<PasskeyChallenge>;
  /** The kind of the org this context reads under, for the org-context empty state (#1205). */
  orgKind?: OrgKind | null;
}) {
  const factors = useStepUpFactors(stepUpFactors);
  const investigateLive = investigateLiveIn(grants?.standing ?? []);
  if (session.workspace_id === null) {
    return (
      <>
        <PageIntro destination="access" />
        <section className="panel">
          <Empty title="Partner access belongs to a workspace">
            {orgKind === "partner" ? (
              <p>
                You are reading as a partner. What you have asked of each customer is on the
                Customers screen; the customer answers here, in its own workspace.
              </p>
            ) : (
              <p>{readingAsOrg(orgKind, "answer what has been asked of it")}</p>
            )}
          </Empty>
        </section>
      </>
    );
  }
  return (
    <>
      <PageIntro
        destination="access"
      />
      {error === null ? null : <Notice tone="bad">{error}</Notice>}
      {grants === null && error === null ? (
        <section className="panel">
          <p className="muted panel__body" role="status">
            Reading this workspace's requests…
          </p>
        </section>
      ) : null}
      {grants === null ? null : (
        <>
            <Waiting
              rows={grants.waiting}
              investigateLive={investigateLive}
              onAnswer={onAnswer}
              factors={factors}
              beginPasskey={beginPasskey}
            />
            <Standing
              rows={grants.standing}
              investigateLive={investigateLive}
              onRevoke={onRevoke}
            />
            <Past rows={grants.past} />
        </>
      )}
    </>
  );
}

/**
 * A partner's Investigate, and the rest. Investigate is how a partner enters the workspace
 * (`auth/service.py`'s context switch), so the rest are used from inside it (#1187, M4).
 */
function splitWayIn(rows: GrantRow[]): [GrantRow | undefined, GrantRow[]] {
  return [
    rows.find((row) => row.capability === "investigate"),
    rows.filter((row) => row.capability !== "investigate"),
  ];
}

const wayInSentence = (org: string) => `The way in: how ${org} enters this workspace.`;

/** What a partner uses once inside, grouped under its Investigate (#1197). */
function InsideGroup({
  org,
  rows,
  children,
}: {
  org: string;
  rows: GrantRow[];
  children: (row: GrantRow) => ReactNode;
}) {
  if (rows.length === 0) return null;
  return (
    <fieldset className="stack stack--tight">
      <legend>What {org} can do once inside</legend>
      {rows.map(children)}
    </fieldset>
  );
}

/** Rows by partner: its id, which tells two partners of the same name apart, and its name. */
function byOrg(rows: GrantRow[]): Array<[string, string, GrantRow[]]> {
  const groups = new Map<string, [string, GrantRow[]]>();
  for (const row of orderedCapabilities(rows)) {
    const [name, held] = groups.get(row.org_id) ?? [row.org_name ?? row.org_id, []];
    groups.set(row.org_id, [name, [...held, row]]);
  }
  return [...groups.entries()].map(([orgId, [name, held]]) => [orgId, name, held]);
}

interface StepUpProps {
  factors: FactorsReading;
  beginPasskey?: BeginPasskey;
}

function Waiting({
  rows,
  investigateLive,
  onAnswer,
  factors,
  beginPasskey,
}: {
  rows: GrantRow[];
  investigateLive: (org: string) => boolean;
  onAnswer: OnAnswer;
} & StepUpProps) {
  return (
    <section className="panel" data-testid="waiting">
      <div className="panel__head">
        <h2>Waiting for your answer</h2>
        <Chip tone={rows.length > 0 ? "warn" : undefined}>{rows.length} waiting</Chip>
      </div>
      {rows.length === 0 ? (
        <Empty title="Nothing waiting">
          <p>When a partner asks to serve this workspace, each capability appears here.</p>
        </Empty>
      ) : (
        byOrg(rows).map(([orgId, org, requests]) => {
          const [wayIn, inside] = splitWayIn(requests);
          const request = (row: GrantRow) => (
            <GrantRequest
              key={row.id}
              row={row}
              investigateLive={investigateLive(orgId)}
              onAnswer={onAnswer}
              factors={factors}
              beginPasskey={beginPasskey}
            />
          );
          return (
            <div key={orgId} className="panel__body stack">
              <h3>{org} asks for:</h3>
              {wayIn === undefined ? null : (
                <div className="stack stack--tight">
                  <p className="muted">{wayInSentence(org)}</p>
                  {request(wayIn)}
                </div>
              )}
              <InsideGroup org={org} rows={inside}>
                {request}
              </InsideGroup>
            </div>
          );
        })
      )}
    </section>
  );
}

/** One capability an organisation asked for, and its answer: the page's, and the Inbox drawer's (#808). */
export function GrantRequest({
  row,
  investigateLive,
  onAnswer,
  factors,
  beginPasskey,
}: {
  row: GrantRow;
  /** Whether the asking partner's Investigate is live; unknown (the Inbox's drawer) says nothing. */
  investigateLive?: boolean;
  onAnswer: OnAnswer;
} & StepUpProps) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"granted" | "declined" | null>(null);
  const readOnly = useReadOnly();
  const { proof, attempt, unproven, setProof, spent } = useStepUpAttempt();
  const text = CAPABILITY_TEXT[row.capability];
  const answer = async (decision: "granted" | "declined") => {
    // Granting is standing authority: no proof, nothing is sent (#659).
    const granting = decision === "granted";
    if (granting && proof === null) return;
    setBusy(decision);
    setError(null);
    try {
      await onAnswer(row.id, decision, granting ? proof : null);
    } catch (failure) {
      setError(messageOf(failure));
    } finally {
      setBusy(null);
      if (granting) spent();
    }
  };
  return (
    <div className="stack stack--tight" data-testid="grant-request">
      <div>
        <strong>{text.title}</strong>
        <p className="muted">{text.detail}</p>
        <small className="muted">
          Asked by {row.requested_by ?? "the partner"} on {row.requested_at?.slice(0, 10)}.
        </small>
        {investigateLive !== undefined && waitsOnInvestigate(row, investigateLive) ? (
          <p>{NO_EFFECT_YET}</p>
        ) : null}
      </div>
      {OPENS_AUDIT_TRAIL.includes(row.capability) ? (
        <Notice>
          Allowing this also lets {row.org_name ?? "this partner"} read this workspace's full audit
          trail: every sign-in, every answer your members give, and every change, for as long as the
          access stands.
        </Notice>
      ) : null}
      {error === null ? null : <Notice tone="bad">{error}</Notice>}
      {readOnly ? null : (
        <>
          <StepUpDialog
            key={attempt}
            purpose={GRANT_STEP_UP_PURPOSE}
            changeId={`grant-${row.id}`}
            factors={factors}
            disabled={busy !== null}
            beginPasskey={beginPasskey}
            onProofChange={setProof}
            onError={setError}
          />
          <div className="actions">
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy !== null || unproven}
              onClick={() => void answer("granted")}
            >
              {busy === "granted" ? "Allowing…" : `Allow ${text.title.toLowerCase()}`}
            </button>
            <button
              type="button"
              className="btn btn--danger"
              disabled={busy !== null}
              onClick={() => void answer("declined")}
            >
              {busy === "declined" ? "Declining…" : "Decline"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function Standing({
  rows,
  investigateLive,
  onRevoke,
}: {
  rows: GrantRow[];
  investigateLive: (org: string) => boolean;
  onRevoke: (grantId: string) => Promise<void>;
}) {
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<{
    orgId: string;
    org: string;
    row: GrantRow;
    others: GrantRow[];
  } | null>(null);
  /** Each partner's Investigate, where the focus lands once its take-back removes the control. */
  const wayIns = useRef(new Map<string, HTMLDivElement>());
  /** Where the focus lands when the take-back leaves the partner nothing here at all. */
  const heading = useRef<HTMLHeadingElement | null>(null);
  const readOnly = useReadOnly();
  const revoke = async (id: string) => {
    setError(null);
    try {
      await onRevoke(id);
    } catch (failure) {
      setError(messageOf(failure));
    }
  };
  return (
    <section className="panel" data-testid="standing">
      {confirming === null ? null : (
        <TakeBackInvestigate
          org={confirming.org}
          others={confirming.others}
          fallback={() => wayIns.current.get(confirming.orgId) ?? heading.current}
          onKeep={() => setConfirming(null)}
          onTakeBack={async () => {
            await revoke(confirming.row.id);
            setConfirming(null);
          }}
        />
      )}
      <div className="panel__head">
        <h2 ref={heading} tabIndex={-1}>
          Standing access
        </h2>
        <Chip>{rows.length} held</Chip>
      </div>
      {error === null ? null : (
        <div className="panel__body">
          <Notice tone="bad">{error}</Notice>
        </div>
      )}
      {rows.length === 0 ? (
        <Empty title="No partner holds anything here">
          <p>Access you allow appears here, and can be taken back at any time.</p>
        </Empty>
      ) : (
        byOrg(rows).map(([orgId, org, held]) => {
          const [wayIn, inside] = splitWayIn(held);
          const line = (row: GrantRow, title: boolean) => (
            <div key={row.id} className="actions" data-testid="held">
              {title ? <strong>{CAPABILITY_TEXT[row.capability].title}</strong> : null}
              <span className="muted">
                Allowed {row.consented_at?.slice(0, 10)} by {row.consented_by}
              </span>
              {waitsOnInvestigate(row, investigateLive(orgId)) ? <span>{NO_EFFECT_YET}</span> : null}
              {readOnly ? null : (
                <button
                  type="button"
                  className="btn btn--small btn--danger"
                  onClick={() =>
                    row.capability === "investigate"
                      ? setConfirming({ orgId, org, row, others: stopsWith(inside) })
                      : void revoke(row.id)
                  }
                >
                  Revoke<span className="sr-only"> {CAPABILITY_TEXT[row.capability].title.toLowerCase()}</span>
                </button>
              )}
            </div>
          );
          return (
            <div key={orgId} className="panel__body stack">
              <h3>{org}</h3>
              <div
                className="stack stack--tight"
                data-testid="way-in"
                tabIndex={-1}
                ref={(element) => {
                  if (element === null) wayIns.current.delete(orgId);
                  else wayIns.current.set(orgId, element);
                }}
              >
                <strong>Investigate</strong>
                <p className="muted">{wayInSentence(org)}</p>
                {wayIn === undefined ? <p>Not allowed.</p> : line(wayIn, false)}
              </div>
              <InsideGroup org={org} rows={inside}>
                {(row) => line(row, true)}
              </InsideGroup>
            </div>
          );
        })
      )}
    </section>
  );
}

/** What stops working when a partner's Investigate is taken back: what it uses from inside. */
const stopsWith = (inside: GrantRow[]) =>
  inside.filter((row) => NEEDS_INVESTIGATE.includes(row.capability));

/** "A", "A and B", "A, B and C". */
const listed = (titles: string[]) =>
  titles.length < 2 ? titles.join("") : `${titles.slice(0, -1).join(", ")} and ${titles[titles.length - 1]}`;

/**
 * Taking a partner's Investigate back, asked first (#1197): it is the way in, so what the
 * partner uses from inside stops too, and the question names it. The focus moves into the
 * question and returns to the control that asked, or, once the take-back has removed that
 * control, to the partner's Investigate.
 */
function TakeBackInvestigate({
  org,
  others,
  fallback,
  onKeep,
  onTakeBack,
}: {
  org: string;
  others: GrantRow[];
  fallback: () => HTMLElement | null;
  onKeep: () => void;
  onTakeBack: () => Promise<void>;
}) {
  const dialog = useRef<HTMLDialogElement | null>(null);
  const heading = useRef<HTMLHeadingElement | null>(null);
  const title = useId();
  const [busy, setBusy] = useState(false);
  const back = useRef(fallback);
  useEffect(() => {
    const element = dialog.current;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (element !== null && !element.open) element.showModal?.();
    heading.current?.focus();
    return () => {
      element?.close?.();
      // The take-back's own re-read removes the opener; the focus waits for it.
      queueMicrotask(() => {
        if (opener?.isConnected) opener.focus();
        else back.current()?.focus();
      });
    };
  }, []);
  const titles = others.map((row) => CAPABILITY_TEXT[row.capability].title);
  return (
    <dialog
      ref={dialog}
      className="modal"
      aria-labelledby={title}
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onKeep();
      }}
    >
      <div className="stack stack--tight">
        <h2 id={title} ref={heading} tabIndex={-1}>
          Take back Investigate from {org}?
        </h2>
        <p>Investigate is how {org} enters this workspace.</p>
        {titles.length === 0 ? null : (
          <p>
            {listed(titles)} {titles.length === 1 ? "stops" : "stop"} working too.
          </p>
        )}
        <div className="actions">
          <button
            type="button"
            className="btn btn--danger"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void onTakeBack();
            }}
          >
            {busy ? "Taking back…" : "Take back Investigate"}
          </button>
          <button type="button" className="btn" disabled={busy} onClick={onKeep}>
            Keep Investigate
          </button>
        </div>
      </div>
    </dialog>
  );
}

function Past({ rows }: { rows: GrantRow[] }) {
  if (rows.length === 0) return null;
  return (
    <section className="panel" data-testid="past">
      <div className="panel__head">
        <h2>History</h2>
        <Chip>{rows.length} answered</Chip>
      </div>
      <div className="table-wrap" tabIndex={0}>
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Partner</th>
              <th scope="col">Capability</th>
              <th scope="col">Outcome</th>
              <th scope="col">When</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{row.org_name ?? row.org_id}</td>
                <td>{CAPABILITY_TEXT[row.capability].title}</td>
                <td>
                  <Chip tone="bad">{row.state}</Chip>{" "}
                  <small className="muted">
                    by {row.state === "revoked" ? row.revoked_by : row.consented_by}
                  </small>
                </td>
                <td>{(row.state === "revoked" ? row.revoked_at : row.consented_at)?.slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
